/**
 * Log Viewer 2 - API Client
 *
 * Maps existing Continuum client to spec interface:
 * - getStreamInfo(processUid, channel) → StreamInfo
 * - subscribe(processUid, channel, fromSeq, toSeq?) → AsyncIterator
 */

import { ContinuumLogClient } from "../../hermesclient/continuum-log-client";
import { ContinuumLogClientSSE } from "../../hermesclient/continuum-log-client-sse";
import { ContinuumApiClient } from "../../hermesclient/continuum-api-client";
import { BufferSource, LogRecord } from "../../hermesclient/proto/continuum_rpc";
import { StreamInfo, SubscribeOptions, LogMessage, LogParserType } from "./types";
import { createAccur8Parser } from "./parsers/accur8-parser";
import { applogRecordToEntry, renderErrorChain } from "./parsers/applog-parser";
import { createJournalParser } from "./parsers/journal-parser";
import { createPlainTextParser } from "./parsers/plain-text-parser";
import { createWindowsEventLogParser } from "./parsers/windows-event-log-parser";

// ============================================================================
// Compiled Regex Patterns
// ============================================================================
const TIMESTAMP_PATTERN = /^\d{2}:\d{2}:\d{2}[.,]\d{3}/;

// ============================================================================
// Reusable Log Parsing Pipeline
// ============================================================================

export interface LogParsingCallbacks {
  emit: (message: LogMessage) => void;
  emitUpdate: (message: LogMessage) => void;
}

/**
 * Encapsulates the log-parsing pipeline so both the live stream (subscribe)
 * and the archive path can share the same text-parsing and continuation logic.
 */
export class LogParsingPipeline {
  private readonly parser: ReturnType<typeof createAccur8Parser>;
  private readonly eventLogParser: ReturnType<typeof createWindowsEventLogParser>;
  private readonly journalParser: ReturnType<typeof createJournalParser>;
  private readonly parserType: LogParserType;
  private readonly id: string;
  private lastMessage: LogMessage | null = null;

  constructor(
    id: string,
    parserType: LogParserType,
  ) {
    this.id = id;
    this.parserType = parserType;
    this.parser = parserType === 'plain-text'
      ? createPlainTextParser(id)
      : createAccur8Parser(id);
    // Per-source parser for Windows Event Log records. The accur8
    // parser's "no leading timestamp = continuation" heuristic
    // would otherwise concatenate every JSON record into one merged
    // LogMessage; this parser handles each Buffer as a self-contained
    // record.
    this.eventLogParser = createWindowsEventLogParser(`${id}-eventlog`);
    // Per-source parser for systemd journald records (service-manager systemd backend).
    // Same one-JSON-record-per-Buffer shape as the event log; without this the
    // accur8 parser smashes every journal record into one giant raw-JSON line.
    this.journalParser = createJournalParser(`${id}-journal`);
  }

  /**
   * Process a single ParsedLogLine through the parser, emitting LogMessages.
   */
  processLog(
    log: {
      text: string;
      timestamp: Date;
      sequence: number;
      streamSeq?: number;
      source: unknown;
      buffer: { data: Uint8Array; record?: LogRecord };
    },
    callbacks: LogParsingCallbacks,
  ): void {
    const blockSize = log.buffer.data.length;

    // A record that names the process run it came from carries that onto every message
    // made from it, whichever parser makes them.
    const processRunUid = log.buffer.record?.processRunUid || undefined;
    if (processRunUid) {
      const inner = callbacks;
      callbacks = {
        emit: (m) => inner.emit({ ...m, processRunUid }),
        emitUpdate: (m) => inner.emitUpdate(m),
      };
    }

    // applog: the buffer carries a STRUCTURED LogRecord (level, caller, thread, the
    // MDC, the exception — all fields), so there is nothing to parse. Routed on the
    // SOURCE, ahead of everything else and regardless of the job's chosen parser: a
    // structured record must never be fed to a regex that digs those same fields back
    // out of a rendered string. That is the whole point of the channel.
    //
    // The same holds for a record on ANY source: a checkpoint process's console carries
    // one LogRecord per printed line (source stdout). A program's line has no leading
    // timestamp, so the text parsers would take every line after the first for a
    // continuation and fold a whole program into one message.
    if (log.buffer.record) {
      const entry = applogRecordToEntry(log.buffer.record, log.timestamp);

      // A record with an exception shows the chain under its message, root last — the
      // same shape `a8 logs --structured` prints, so the CLI and the UI tell the same
      // story. errorChain also rides on the message for a richer rendering later.
      const text =
        entry.errorChain.length > 0
          ? `${entry.message}\n${renderErrorChain(entry.errorChain)}`
          : entry.message;

      const message: LogMessage = {
        natsSeq: log.sequence,
        recordSeq: log.streamSeq || log.sequence,
        baseDate: entry.timestamp.getTime(),
        text,
        originalLine: entry.originalLine,
        timestamp: entry.timestamp,
        source: String(log.source),
        level: entry.level,
        category: entry.category,
        thread: entry.thread,
        metadata: entry.metadata,
        errorChain: entry.errorChain.length > 0 ? entry.errorChain : undefined,
        droppedRecords: entry.droppedRecords > 0 ? entry.droppedRecords : undefined,
        sseBlockSize: blockSize,
        lineSeq: -1,
      };
      callbacks.emit(message);
      // Deliberately NOT setting lastMessage: applog records are self-contained, so
      // the continuation heuristic must never attach a following line to one.
      this.lastMessage = null;
      return;
    }

    // Source-aware routing: Windows Event Log records are one JSON
    // document per Buffer with no embedded newlines. The accur8
    // structured parser would treat each JSON record as a continuation
    // of the previous LogMessage (no leading HH:MM:SS timestamp) and
    // smash them all into one entry. Route to a dedicated parser that
    // emits one LogMessage per record without any continuation logic.
    if (log.source === BufferSource.windows_event_log) {
      const entries = this.eventLogParser.parseLine(log.text, log.timestamp);
      for (const entry of entries) {
        const message: LogMessage = {
          natsSeq: log.sequence,
          recordSeq: log.streamSeq || log.sequence,
          baseDate: log.timestamp.getTime(),
          text: entry.message,
          originalLine: entry.originalLine,
          timestamp: entry.timestamp,
          source: String(log.source),
          level: entry.level,
          category: entry.category,
          thread: entry.thread,
          sseBlockSize: blockSize,
          lineSeq: -1,
        };
        callbacks.emit(message);
        this.lastMessage = message;
      }
      return;
    }

    // systemd journald: one JSON record per Buffer, same as the event log.
    // Route to the dedicated parser so each record is its own LogMessage
    // (MESSAGE surfaced) instead of being concatenated as raw JSON.
    if (log.source === BufferSource.journalctl) {
      const entries = this.journalParser.parseLine(log.text, log.timestamp);
      for (const entry of entries) {
        const message: LogMessage = {
          natsSeq: log.sequence,
          recordSeq: log.streamSeq || log.sequence,
          baseDate: log.timestamp.getTime(),
          text: entry.message,
          originalLine: entry.originalLine,
          timestamp: entry.timestamp,
          source: String(log.source),
          level: entry.level,
          category: entry.category,
          thread: entry.thread,
          sseBlockSize: blockSize,
          lineSeq: -1,
        };
        callbacks.emit(message);
        this.lastMessage = message;
      }
      return;
    }

    // Per-job default of journal/eventlog: a buffer that did NOT declare its
    // source (source-based routing above already handled declared ones) is
    // parsed with the job's chosen JSON parser. Buffers that DO declare a source
    // always win above, so this only affects un-sourced buffers — it never
    // overrides correct journal/eventlog rendering.
    if (this.parserType === 'journal' || this.parserType === 'eventlog') {
      const recordParser =
        this.parserType === 'journal' ? this.journalParser : this.eventLogParser;
      const entries = recordParser.parseLine(log.text, log.timestamp);
      for (const entry of entries) {
        const message: LogMessage = {
          natsSeq: log.sequence,
          recordSeq: log.streamSeq || log.sequence,
          baseDate: log.timestamp.getTime(),
          text: entry.message,
          originalLine: entry.originalLine,
          timestamp: entry.timestamp,
          source: String(log.source),
          level: entry.level,
          category: entry.category,
          thread: entry.thread,
          sseBlockSize: blockSize,
          lineSeq: -1,
        };
        callbacks.emit(message);
        this.lastMessage = message;
      }
      return;
    }

    const lines = log.text.split('\n');

    // Check if first non-empty line is a continuation (updates last message from previous block)
    let firstLineProcessed = false;
    let firstNonEmptyLine: string | undefined;
    if (this.parserType !== 'plain-text') {
      firstNonEmptyLine = lines.find((l: string) => l.trim());
      if (firstNonEmptyLine && this.lastMessage) {
        const hasTimestamp = TIMESTAMP_PATTERN.test(firstNonEmptyLine);
        if (!hasTimestamp) {
          this.lastMessage.text += '\n' + firstNonEmptyLine;
          if (this.lastMessage.originalLine) {
            this.lastMessage.originalLine += '\n' + firstNonEmptyLine;
          }
          callbacks.emitUpdate(this.lastMessage);
          firstLineProcessed = true;
        }
      }
    }

    for (const line of lines) {
      if (!line.trim()) continue;
      if (firstLineProcessed && line === firstNonEmptyLine) {
        firstLineProcessed = false;
        continue;
      }

      const parsedEntries = this.parser.parseLine(line, log.timestamp);

      for (const entry of parsedEntries) {
        const message: LogMessage = {
          natsSeq: log.sequence,
          recordSeq: log.streamSeq || log.sequence,
          baseDate: log.timestamp.getTime(),
          text: entry.message,
          originalLine: entry.originalLine,
          timestamp: entry.timestamp,
          source: String(log.source),
          level: entry.level,
          category: entry.category,
          thread: entry.thread,
          sseBlockSize: blockSize,
          lineSeq: -1,
        };

        callbacks.emit(message);
        this.lastMessage = message;

        if (this.parserType !== 'plain-text' && entry.continuationLines) {
          for (const contLine of entry.continuationLines) {
            const contMessage: LogMessage = {
              natsSeq: log.sequence,
              recordSeq: log.streamSeq || log.sequence,
              baseDate: log.timestamp.getTime(),
              text: contLine.message,
              originalLine: contLine.originalLine,
              timestamp: contLine.timestamp,
              source: String(log.source),
              level: contLine.level,
              category: contLine.category,
              thread: contLine.thread,
              isContinuation: true,
              sseBlockSize: blockSize,
              lineSeq: -1,
            };
            callbacks.emit(contMessage);
          }
        }
      }
    }
  }

  /**
   * Flush any pending parsed entries (call at end of stream or bounded completion).
   */
  flush(
    fallbackSeq: number,
    fallbackTimestamp: number,
    fallbackSource: string,
    callbacks: LogParsingCallbacks,
  ): void {
    const flushed = this.parser.flush();
    for (const entry of flushed) {
      const message: LogMessage = {
        natsSeq: fallbackSeq,
        recordSeq: fallbackSeq,
        baseDate: fallbackTimestamp,
        text: entry.message,
        originalLine: entry.originalLine,
        timestamp: entry.timestamp,
        source: fallbackSource,
        level: entry.level,
        category: entry.category,
        thread: entry.thread,
        lineSeq: -1,
      };
      callbacks.emit(message);

      if (this.parserType !== 'plain-text' && entry.continuationLines) {
        for (const contLine of entry.continuationLines) {
          const contMessage: LogMessage = {
            natsSeq: fallbackSeq,
            recordSeq: fallbackSeq,
            baseDate: fallbackTimestamp,
            text: contLine.message,
            originalLine: contLine.originalLine,
            timestamp: contLine.timestamp,
            source: fallbackSource,
            level: contLine.level,
            category: contLine.category,
            thread: contLine.thread,
            isContinuation: true,
            lineSeq: -1,
          };
          callbacks.emit(contMessage);
        }
      }
    }
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Pull a {channel: bytes} map out of processrun.extra_data.channelStreamInfo.
 *
 * Shape produced by pkg/registry/health_checker.go's channel-size flusher
 * (Go ChannelStreamInfo struct with json tags `msgs`/`bytes`/`firstSeq`/
 * `lastSeq` — all lowercase):
 *
 *   { stdout: { msgs, bytes, firstSeq, lastSeq }, stderr: { ... }, ... }
 *
 * Stream-summary consumers want a flat Record<channelName, byteCount>, so we
 * map each entry to its bytes value. Returns undefined if the source is
 * missing — callers tolerate that (the worker just doesn't populate
 * totalBytes).
 */
function extractChannelBytes(
  channelStreamInfo: unknown,
): Record<string, number> | undefined {
  if (!channelStreamInfo || typeof channelStreamInfo !== "object") {
    return undefined;
  }
  const out: Record<string, number> = {};
  for (const [channel, info] of Object.entries(
    channelStreamInfo as Record<string, unknown>,
  )) {
    if (info && typeof info === "object" && "bytes" in info) {
      const bytes = (info as { bytes: unknown }).bytes;
      if (typeof bytes === "number") {
        out[channel] = bytes;
      }
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

// ============================================================================
// API Wrapper
// ============================================================================

export class LogViewerApi {
  private apiClient?: ContinuumApiClient;
  private logClient?: ContinuumLogClient | ContinuumLogClientSSE;

  constructor(client: ContinuumLogClient | ContinuumLogClientSSE | ContinuumApiClient) {
    if (client instanceof ContinuumApiClient) {
      this.apiClient = client;
    } else {
      this.logClient = client;
    }
  }

  /**
   * Get stream info (maxSeq, minSeq, createdAt)
   */
  async getStreamInfo(processUid: string, channel: string): Promise<StreamInfo> {
    const client = this.apiClient || this.logClient!;
    const info = await client.getChannelInfo(processUid, channel);

    return {
      maxSeq: info.lastSequence,
      minSeq: info.firstSequence,
      createdAt: info.firstTimestamp,
      messageCount: info.messageCount,
      serviceLabel: info.serviceLabel,
      startedAt: info.processRun?.startedAt,
      completedAt: info.processRun?.completedAt,
      channelSizes: extractChannelBytes(info.processRun?.extraData?.channelStreamInfo),
      logarchive: info.processRun?.logarchive,
    };
  }

  /**
   * Subscribe to log stream with fromSeq/toSeq
   *
   * Returns an async iterator of log messages.
   * If toSeq is provided, stops after reaching that sequence (bounded mode).
   */
  async subscribe(options: SubscribeOptions): Promise<LogStream> {
    const { processUid, channel, fromSeq, toSeq, onUpdate, parserType } = options;

    if (!this.logClient) {
      throw new Error("Subscribe requires a log client");
    }

    const stream = new LogStream(toSeq);
    const pipeline = new LogParsingPipeline(`${processUid}-${channel}`, parserType);
    let hasCompleted = false;

    const callbacks: LogParsingCallbacks = {
      emit: (msg) => stream.push(msg),
      emitUpdate: (msg) => stream.pushUpdate(msg),
    };

    const subscribeOptions = {
      processUid,
      channel,
      startSeq: String(fromSeq),
      endSeq: toSeq !== undefined ? String(toSeq) : "tail",
    };
    const unsubscribe = await this.logClient.streamLogs(
      // subscribeOptions carries startSeq/endSeq (strings) but LogStreamOptions
      // declares startSequence (number) — a real shape divergence the client
      // resolves at runtime. Left as an unchecked bridge; reconciling the two
      // shapes is its own change.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      subscribeOptions as any,
      (log) => {
        if (hasCompleted) return;

        pipeline.processLog(log, callbacks);

        // Auto-close when reaching toSeq (bounded mode)
        if (toSeq !== undefined && log.sequence >= toSeq) {
          if (hasCompleted) return;
          hasCompleted = true;
          pipeline.flush(log.sequence, log.timestamp.getTime(), String(log.source), callbacks);
          unsubscribe();
          stream.close();
        }
      },
      () => {
        if (hasCompleted) return;
        hasCompleted = true;
        pipeline.flush(0, Date.now(), processUid, callbacks);
        stream.close();
      }
    );

    stream.setUnsubscribe(unsubscribe);
    if (onUpdate) {
      stream.setOnUpdate(onUpdate);
    }

    return stream;
  }
}

// ============================================================================
// Log Stream (Async Iterator)
// ============================================================================

export class LogStream {
  private buffer: LogMessage[] = [];
  private closed = false;
  private waiting: ((value: IteratorResult<LogMessage>) => void) | null = null;
  private unsubscribe: (() => void) | null = null;
  private onUpdate?: (message: LogMessage) => void;

  constructor(private toSeq?: number) {}

  setUnsubscribe(fn: () => void) {
    this.unsubscribe = fn;
  }

  setOnUpdate(fn: (message: LogMessage) => void) {
    this.onUpdate = fn;
  }

  push(message: LogMessage) {
    if (this.closed) {
      console.warn(`[LogStream] Attempted to push message to closed stream, natsSeq=${message.natsSeq}`);
      return;
    }

    if (this.waiting) {
      // Someone is waiting, deliver immediately
      this.waiting({ value: message, done: false });
      this.waiting = null;
    } else {
      // Buffer for later
      this.buffer.push(message);
    }
  }

  pushUpdate(message: LogMessage) {
    if (this.onUpdate) {
      this.onUpdate(message);
    }
  }

  close() {
    this.closed = true;

    // If someone is waiting and we have buffered messages, deliver one instead of marking done
    if (this.waiting && this.buffer.length > 0) {
      const message = this.buffer.shift()!;
      this.waiting({ value: message, done: false });
      this.waiting = null;
    } else if (this.waiting) {
      // No buffered messages, mark as done
      this.waiting({ value: undefined, done: true });
      this.waiting = null;
    }

    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  async next(): Promise<IteratorResult<LogMessage>> {
    // Return buffered message if available
    if (this.buffer.length > 0) {
      const message = this.buffer.shift()!;
      return { value: message, done: false };
    }

    // Stream closed AND no more buffered messages
    if (this.closed) {
      return { value: undefined, done: true };
    }

    // Wait for next message
    return new Promise((resolve) => {
      this.waiting = resolve;
    });
  }

  [Symbol.asyncIterator](): AsyncIterator<LogMessage> {
    return this;
  }
}

// ============================================================================
// Standalone Helper Functions
// ============================================================================

/**
 * Get stream info without needing to create a full LogViewerApi instance.
 * Used for resolving keywords before starting stream.
 */
export async function getStreamInfo(
  processUid: string,
  channel: string,
  authToken: string,
  hermesUrl: string
): Promise<StreamInfo> {
  const apiClient = new ContinuumApiClient({
    authToken,
    baseUrl: hermesUrl,
  });

  const api = new LogViewerApi(apiClient);
  return api.getStreamInfo(processUid, channel);
}
